import { Timestamp, GeoPoint } from "firebase/firestore";
import { Post } from "@/types/post";

// Versioned key. Bump the suffix if the serialized shape changes so old caches
// are ignored instead of mis-deserialized.
const STORAGE_KEY = "debari.feed.cache.v1";
const MAX_CACHED_POSTS = 30;

type SerializedTimestamp = { _t: "ts"; ms: number };
type SerializedGeoPoint = { _t: "geo"; lat: number; lng: number };

const isStorageAvailable = (): boolean => {
  return typeof window !== "undefined" && !!window.sessionStorage;
};

const serializeTimestamp = (ts: unknown): SerializedTimestamp | undefined => {
  if (!ts) return undefined;
  const t = ts as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
  if (typeof t.toMillis === "function") {
    return { _t: "ts", ms: t.toMillis() };
  }
  if (typeof t.seconds === "number") {
    return {
      _t: "ts",
      ms: t.seconds * 1000 + Math.floor((t.nanoseconds ?? 0) / 1_000_000),
    };
  }
  return undefined;
};

const deserializeTimestamp = (v: unknown): Timestamp | undefined => {
  if (!v || typeof v !== "object") return undefined;
  const tagged = v as SerializedTimestamp;
  if (tagged._t !== "ts" || typeof tagged.ms !== "number") return undefined;
  return Timestamp.fromMillis(tagged.ms);
};

const serializeGeoPoint = (gp: unknown): SerializedGeoPoint | undefined => {
  if (!gp) return undefined;
  const g = gp as { latitude?: number; longitude?: number };
  if (typeof g.latitude !== "number" || typeof g.longitude !== "number") {
    return undefined;
  }
  return { _t: "geo", lat: g.latitude, lng: g.longitude };
};

const deserializeGeoPoint = (v: unknown): GeoPoint | undefined => {
  if (!v || typeof v !== "object") return undefined;
  const tagged = v as SerializedGeoPoint;
  if (tagged._t !== "geo") return undefined;
  if (typeof tagged.lat !== "number" || typeof tagged.lng !== "number") {
    return undefined;
  }
  return new GeoPoint(tagged.lat, tagged.lng);
};

const serializePost = (post: Post): unknown => {
  const { createdAt, eventDate, location, ...rest } = post;
  return {
    ...rest,
    createdAt: serializeTimestamp(createdAt),
    eventDate: serializeTimestamp(eventDate),
    location: location
      ? {
          city: location.city,
          country: location.country,
          geoPoint: serializeGeoPoint(location.geoPoint),
        }
      : undefined,
  };
};

const deserializePost = (raw: unknown): Post | null => {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown> & {
    location?: { city?: string; country?: string; geoPoint?: unknown };
  };
  if (typeof r.id !== "string") return null;
  // createdAt is required by the Post type. Without it the feed sort order and
  // formatted-date displays would be wrong, so drop the entry instead of fudging.
  const createdAt = deserializeTimestamp(r.createdAt);
  if (!createdAt) return null;
  return {
    ...(r as unknown as Post),
    createdAt,
    eventDate: deserializeTimestamp(r.eventDate),
    location: r.location
      ? {
          city: r.location.city,
          country: r.location.country,
          geoPoint: deserializeGeoPoint(r.location.geoPoint),
        }
      : undefined,
  };
};

export function readFeedCache(): Post[] {
  if (!isStorageAvailable()) return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const out: Post[] = [];
    for (const entry of parsed) {
      const post = deserializePost(entry);
      if (post) out.push(post);
    }
    return out;
  } catch {
    return [];
  }
}

export function writeFeedCache(posts: Post[]): void {
  if (!isStorageAvailable()) return;
  try {
    const slim = posts.slice(0, MAX_CACHED_POSTS).map(serializePost);
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
  } catch {
    // sessionStorage write errors (quota, privacy mode) must not break the UI.
  }
}

export function clearFeedCache(): void {
  if (!isStorageAvailable()) return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
